import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, For, getOwner, Match, runWithOwner, Switch } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import SearchBar from "../../../components/searchBar/SearchBar";
import { toFavourite } from "../../../utils/mappers/favourite";
import FavouriteService from "../../../services/FavouriteService";
import { softContains } from "../../../utils/stringUtil";
import { isEmpty } from "lodash-es";
import { DestructiveModal } from "../../../components/modal/Modal";
import { useFormServices } from "../../../services/formServices";
import SubformService from "../../../services/SubformService";
import EmptyState from "../../../components/emptyState/EmptyState";
import { useModalManager } from "../../../components/modal/ModalManager";
import { scrollToField } from "../../../utils/fieldUtil";

const EditorMenuFavourites: VoidComponent = () => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { fieldService, currentScope, ruleService } = useFormServices();
  const favouriteQuery = FavouriteService.getAll();
  const [query, setQuery] = createSignal("");
  const owner = getOwner();
  const favourites = createMemo(() =>
    favouriteQuery.data
      ?.map((favouriteDto) => toFavourite(favouriteDto))
      .filter(({ name }) => softContains(name, query())),
  );

  const favouriteMenuItems = (
    <ul class="flex h-full flex-1 flex-col gap-1 overflow-y-auto py-3 -outline-offset-1 focus-visible:outline-default">
      <For each={favourites()}>
        {(favourite) => {
          const field = favourite.field;
          return (
            <li aria-label={favourite.name} class="first:-mt-2">
              <field.FavouriteMenuItem
                label={favourite.name}
                addFunction={() => {
                  runWithOwner(owner, () => {
                    const domainObjects = FavouriteService.restore(favourite, currentScope);
                    domainObjects.subforms.forEach(SubformService.addSubform);
                    domainObjects.rules.forEach(ruleService.addToEndWithScope);
                    domainObjects.fields.forEach((field) => {
                      fieldService.addFieldToEnd(field);
                    });
                    const lastField = domainObjects.fields.at(-1);
                    if (!isEmpty(lastField)) {
                      scrollToField(lastField.uid, "smooth");
                    }
                  });
                }}
                onEdit={(newName) => {
                  runWithOwner(owner, () => {
                    void FavouriteService.update(favourite.id, newName);
                  });
                }}
                onDelete={() => {
                  open({
                    content: () => (
                      <DestructiveModal
                        cancelLabel={t(($) => $.CANCEL_BUTTON)}
                        confirmLabel={t(($) => $.DELETE_BUTTON)}
                        title={t(($) => $.FAVOURITE_DELETE_MODAL_TITLE)}
                        description={t(($) => $.FAVOURITE_DELETE_MODAL_DESCRIPTION, { name: favourite.name })}
                        onConfirm={() => {
                          runWithOwner(owner, () => {
                            void FavouriteService.delete(favourite.id);
                          });
                        }}
                      />
                    ),
                  });
                }}
                favourite={favourite}
              />
            </li>
          );
        }}
      </For>
    </ul>
  );

  return (
    <div class="mx-3">
      <div class="mb-3">
        <SearchBar
          query={query()}
          onQueryChange={(value) => {
            setQuery(value);
          }}
          placeholder={t(($) => $.FAVOURITES_SEARCH_PLACEHOLDER)}
        />
      </div>
      <Switch>
        <Match when={favouriteQuery.isSuccess && !isEmpty(favourites())}>{favouriteMenuItems}</Match>
        <Match when={favouriteQuery.isSuccess && isEmpty(favourites())}>
          <EmptyState
            title={t(($) => $.FAVOURITES_SEARCH_EMPTY_STATE_TITLE)}
            description={t(($) => $.FAVOURITES_SEARCH_EMPTY_STATE_DESCRIPTION)}
          />
        </Match>
      </Switch>
    </div>
  );
};

export default EditorMenuFavourites;
